/**
 * Centralized Navigation Service
 * Handles all back button logic and URL state management
 */

interface NavigationOptions {
  replace?: boolean;
  state?: Record<string, any>;
}

export class NavigationService {
  /**
   * Go back one step in browser history
   * @param {string} fallbackPath - Path to navigate to if no history exists
   */
  static goBack(fallbackPath: string = '/'): void {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = fallbackPath;
    }
  }

  /**
   * Go back to a specific page
   * @param {string} path - Path to navigate to
   * @param {Object} options - Navigation options
   */
  static goTo(path: string, options: NavigationOptions = {}): void {
    const { replace = false, state = {} } = options;
    
    if (replace) {
      window.history.replaceState(state, '', path);
    } else {
      window.history.pushState(state, '', path);
    }
  }

  /**
   * Clear specific URL parameters
   * @param {Array} paramsToClear - Array of parameter names to clear
   * @param {URLSearchParams} currentParams - Current search params
   * @returns {URLSearchParams} - New search params with cleared values
   */
  static clearURLParams(paramsToClear: string[], currentParams: URLSearchParams): URLSearchParams {
    const newSearchParams = new URLSearchParams(currentParams);
    
    paramsToClear.forEach(param => {
      newSearchParams.delete(param);
    });
    
    return newSearchParams;
  }

  /**
   * Set specific URL parameters
   * @param {Object} params - Object with parameter names and values
   * @param {URLSearchParams} currentParams - Current search params
   * @returns {URLSearchParams} - New search params with set values
   */
  static setURLParams(params: Record<string, string | null | undefined>, currentParams: URLSearchParams): URLSearchParams {
    const newSearchParams = new URLSearchParams(currentParams);
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
    });
    
    return newSearchParams;
  }

  /**
   * Get the previous page from session storage
   * @returns {string|null} - Previous page path or null
   */
  static getPreviousPage(): string | null {
    return sessionStorage.getItem('previousPage');
  }

  /**
   * Set the current page as previous page for future back navigation
   * @param {string} currentPath - Current page path
   */
  static setPreviousPage(currentPath: string): void {
    sessionStorage.setItem('previousPage', currentPath);
  }

  /**
   * Clear the stored previous page
   */
  static clearPreviousPage(): void {
    sessionStorage.removeItem('previousPage');
  }

  /**
   * Check if we can go back (has history)
   * @returns {boolean} - True if can go back
   */
  static canGoBack(): boolean {
    return window.history.length > 1;
  }

  /**
   * Get current URL path
   * @returns {string} - Current path
   */
  static getCurrentPath(): string {
    return window.location.pathname;
  }

  /**
   * Get current URL with search params
   * @returns {string} - Current URL
   */
  static getCurrentURL(): string {
    return window.location.href;
  }
} 